import * as React from 'react'
import {
    ColumnDef,
    getSortedRowModel,
    SortingState,
    type Row,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
    type PaginationState,
} from '@tanstack/react-table'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/components/ui/pagination'

type ServerPaginationProps = {
    enablePagination: true
    serverPagination: true
    pageIndex: number
    pageSize: number
    pageCount: number
    onPaginationChange: (
        updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => void
}

type ClientPaginationProps = {
    enablePagination: true
    serverPagination?: false
    initialPageIndex?: number
    initialPageSize?: number
}

type NoPaginationProps = {
    enablePagination?: false
}

type DataTableBaseProps<TData> = {
    columns: ColumnDef<TData, any>[]
    data: TData[]
    emptyMessage?: string
    onRowClick?: (row: Row<TData>) => void
    scrollContainerClassName?: string // optional: controls max height/scrolling of body
    // Loading
    isLoading?: boolean
    loadingMessage?: string
    // Row count column
    rowCountColumn?: boolean
    // Sorting
    serverSorting?: boolean
    sorting?: SortingState
    onSortingChange?: (updater: SortingState | ((old: SortingState) => SortingState)) => void
    // Expansion
    expandableContent?: (row: Row<TData>) => React.ReactNode
    // Arbitrary metadata available in column renderers/footers via table.options.meta
    meta?: any
}

type DataTableProps<TData> = DataTableBaseProps<TData> & (
    | NoPaginationProps
    | ClientPaginationProps
    | ServerPaginationProps
)

export function DataTable<TData>(props: DataTableProps<TData>) {
    const { columns, data, emptyMessage = 'No results.', onRowClick, expandableContent } = props
    const [internalSorting, setInternalSorting] = React.useState<SortingState>([])
    const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
        pageIndex: props.enablePagination && !props.serverPagination ? (('initialPageIndex' in props ? props.initialPageIndex : undefined) ?? 0) : 0,
        pageSize: props.enablePagination && !props.serverPagination ? (('initialPageSize' in props ? props.initialPageSize : undefined) ?? 10) : 10,
    })
    const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})
    const hasRowCount = !!props.rowCountColumn

    const isServerPagination = props.enablePagination && 'serverPagination' in props && props.serverPagination

    const paginationState: PaginationState | undefined = props.enablePagination
        ? isServerPagination
            ? { pageIndex: (props as ServerPaginationProps).pageIndex, pageSize: (props as ServerPaginationProps).pageSize }
            : internalPagination
        : undefined

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting: props.serverSorting ? (props.sorting ?? []) : internalSorting,
            ...(props.enablePagination ? { pagination: paginationState } : {}),
        },
        onSortingChange: props.serverSorting && props.onSortingChange ? props.onSortingChange : setInternalSorting,
        ...(props.enablePagination && isServerPagination
            ? {
                manualPagination: true,
                pageCount: (props as ServerPaginationProps).pageCount,
                onPaginationChange: (props as ServerPaginationProps).onPaginationChange,
            }
            : props.enablePagination
            ? { onPaginationChange: setInternalPagination }
            : {}),
        getCoreRowModel: getCoreRowModel(),
        ...(props.enablePagination && !isServerPagination
            ? { getPaginationRowModel: getPaginationRowModel() }
            : {}),
        ...(props.serverSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
        ...(props.meta ? { meta: props.meta } : {}),
    })

    const canPrev = props.enablePagination
        ? props.serverPagination
            ? props.pageIndex > 0
            : table.getCanPreviousPage()
        : false

    const canNext = props.enablePagination
        ? props.serverPagination
            ? props.pageIndex + 1 < props.pageCount
            : table.getCanNextPage()
        : false

    const hasExpandable = typeof expandableContent === 'function'

    const toggleExpanded = (rowId: string) =>
        setExpanded((prev) => ({ ...prev, [rowId]: !prev[rowId] }))

    const colSpanAll = columns.length + (hasExpandable ? 1 : 0) + (hasRowCount ? 1 : 0)

    return (
        <div>
            <div className='relative border rounded-xl'>
                <div className={props.scrollContainerClassName ?? 'max-h-[600px] overflow-auto'}>
                    <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {hasExpandable && <TableHead className='sticky top-0 z-10 bg-white w-8' />}
                                {hasRowCount && (
                                    <TableHead className='sticky top-0 z-10 bg-white w-12 text-right'>
                                        #
                                    </TableHead>
                                )}
                                {headerGroup.headers.map((header) => {
                                    const isSorted = header.column.getIsSorted()
                                    return (
                                        <TableHead key={header.id} className='sticky top-0 z-10 bg-white'>
                                            {header.isPlaceholder ? null : header.column.getCanSort() ? (
                                                <Button
                                                    variant='ghost'
                                                    className={`-ml-3 h-8 hover:bg-accent ${isSorted ? 'bg-accent/50' : ''}`}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <span className={isSorted ? 'font-semibold' : ''}>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </span>
                                                    {isSorted === 'asc' ? (
                                                        <ArrowUp className='ml-2 h-4 w-4' />
                                                    ) : isSorted === 'desc' ? (
                                                        <ArrowDown className='ml-2 h-4 w-4' />
                                                    ) : (
                                                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                                                    )}
                                                </Button>
                                            ) : (
                                                flexRender(header.column.columnDef.header, header.getContext())
                                            )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <React.Fragment key={row.id}>
                                    <TableRow
                                        className={`${onRowClick ? 'p-1 cursor-pointer hover:bg-accent/60' : 'p-1'} ${row.index % 2 === 1 ? 'bg-muted' : ''}`}
                                        data-state={row.getIsSelected() && 'selected'}
                                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    >
                                        {hasExpandable && (
                                            <TableCell className='w-8'>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    className='h-6 w-6 p-0'
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleExpanded(row.id)
                                                    }}
                                                    aria-label={expanded[row.id] ? 'Collapse row' : 'Expand row'}
                                                >
                                                    {expanded[row.id] ? (
                                                        <ChevronDown className='h-4 w-4' />
                                                    ) : (
                                                        <ChevronRight className='h-4 w-4' />
                                                    )}
                                                </Button>
                                            </TableCell>
                                        )}
                                        {hasRowCount && (
                                            <TableCell className='w-12 text-right text-muted-foreground'>
                                                {(() => {
                                                    const baseIndex = props.enablePagination
                                                        ? ('serverPagination' in props && props.serverPagination)
                                                            ? props.pageIndex * props.pageSize
                                                            : table.getState().pagination.pageIndex * table.getState().pagination.pageSize
                                                        : 0
                                                    return baseIndex + row.index + 1
                                                })()}
                                            </TableCell>
                                        )}
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {hasExpandable && expanded[row.id] && (
                                        <TableRow className='bg-muted/30'>
                                            <TableCell colSpan={colSpanAll}>
                                                {expandableContent!(row)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={colSpanAll} className='h-24 text-center text-muted-foreground'>
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter className='sticky bottom-0 bg-white border-t border-gray-200'>
                        {table.getFooterGroups().map((footerGroup) => (
                            <TableRow key={footerGroup.id}>
                                {hasExpandable && <TableCell className='sticky bottom-0 z-10 bg-white w-8' />}
                                {hasRowCount && <TableCell className='sticky bottom-0 z-10 bg-white w-12' />}
                                {footerGroup.headers.map((header) => (
                                    <TableCell key={header.id} className='sticky bottom-0 z-10 bg-white font-semibold'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.footer, header.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableFooter>
                </Table>
                </div>
                {props.isLoading && (
                    <div className='absolute inset-0 z-20 bg-white/60 backdrop-blur-[1px] flex items-center justify-center pointer-events-none'>
                        <div className='flex items-center gap-2 text-muted-foreground'>
                            <Loader2 className='h-5 w-5 animate-spin' />
                            <span>{props.loadingMessage ?? 'Loading...'}</span>
                        </div>
                    </div>
                )}
            </div>
            {props.enablePagination && (
                <div className='py-4'>
                    {(() => {
                        const isServer = 'serverPagination' in props && props.serverPagination
                        const totalPages = isServer
                            ? Math.max(props.pageCount, 1)
                            : Math.max(table.getPageCount(), 1)
                        const currentPage = isServer
                            ? props.pageIndex
                            : table.getState().pagination.pageIndex

                        const goToPage = (pageIndex: number) => {
                            if (pageIndex < 0 || pageIndex >= totalPages) return
                            if (isServer) {
                                props.onPaginationChange((p) => ({
                                    ...((p as PaginationState) ?? { pageIndex: props.pageIndex, pageSize: props.pageSize }),
                                    pageIndex,
                                    pageSize: isServer ? props.pageSize : table.getState().pagination.pageSize,
                                }))
                            } else {
                                table.setPageIndex(pageIndex)
                            }
                        }

                        const goPrev = () => goToPage(currentPage - 1)
                        const goNext = () => goToPage(currentPage + 1)

                        const buildPageItems = (): (number | 'ellipsis')[] => {
                            const items: (number | 'ellipsis')[] = []
                            if (totalPages <= 7) {
                                for (let i = 1; i <= totalPages; i++) items.push(i)
                                return items
                            }
                            const first = 1
                            const last = totalPages
                            const c = currentPage + 1 // 1-based
                            items.push(first)
                            if (c > 3) items.push('ellipsis')
                            const windowStart = Math.max(2, c - 1)
                            const windowEnd = Math.min(last - 1, c + 1)
                            for (let i = windowStart; i <= windowEnd; i++) items.push(i)
                            if (c < last - 2) items.push('ellipsis')
                            items.push(last)
                            return items
                        }

                        const pageItems = buildPageItems()
                        const prevDisabled = !canPrev
                        const nextDisabled = !canNext

                        return (
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            href='#'
                                            className={prevDisabled ? 'pointer-events-none opacity-50' : undefined}
                                            onClick={prevDisabled ? undefined : (e) => { e.preventDefault(); goPrev() }}
                                        />
                                    </PaginationItem>
                                    {pageItems.map((item, idx) => (
                                        <PaginationItem key={`${item}-${idx}`}>
                                            {item === 'ellipsis' ? (
                                                <PaginationEllipsis />
                                            ) : (
                                                <PaginationLink
                                                    href='#'
                                                    isActive={item - 1 === currentPage}
                                                    onClick={(e) => { e.preventDefault(); goToPage(item - 1) }}
                                                >
                                                    {item}
                                                </PaginationLink>
                                            )}
                                        </PaginationItem>
                                    ))}
                                    <PaginationItem>
                                        <PaginationNext
                                            href='#'
                                            className={nextDisabled ? 'pointer-events-none opacity-50' : undefined}
                                            onClick={nextDisabled ? undefined : (e) => { e.preventDefault(); goNext() }}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        )
                    })()}
                </div>
            )}
        </div>
    )
}


